import { z } from 'zod/v4';
import { AiEntityType, AnalyticsArtifactType } from './aiEntities';
import { ChartSourceType } from './content';
import type { OpenCodeMessage, OpenCodeMessagePart } from './opencode';
import { AppConnectorMethod } from './projects';

export type AiConnectorSelection = {
    /** Omitted for a standalone custom MCP server. */
    appKey?: string;
    connectionUuid: string;
    connectionMethod: AppConnectorMethod;
    label: string;
};

export const aiConnectorSelectionSchema: z.ZodType<AiConnectorSelection> = z
    .object({
        appKey: z.string().trim().min(1).max(200).optional(),
        connectionUuid: z.string().uuid(),
        connectionMethod: z.nativeEnum(AppConnectorMethod),
        label: z.string().trim().min(1).max(500),
    })
    .refine(
        (selection) =>
            selection.appKey !== undefined ||
            selection.connectionMethod === AppConnectorMethod.GENERIC_MCP,
        {
            message: 'An app is required for this connection method.',
        },
    );

export const AI_STRUCTURED_CONTEXT_METADATA_KEY =
    'sortment.structuredContext.v1';
export const AI_STRUCTURED_CONTEXT_VERSION = 1 as const;

export enum AiStructuredMessagePartType {
    MARKDOWN = 'markdown',
    REFERENCE = 'reference',
}

export enum AiStructuredReferenceEntityType {
    HOME_FEED_ITEM = 'home_feed_item',
}

export type AiStructuredMarkdownPart = {
    type: AiStructuredMessagePartType.MARKDOWN;
    content: string;
};

export type AiStructuredReferencePart = {
    type: AiStructuredMessagePartType.REFERENCE;
    entityType: AiEntityType | AiStructuredReferenceEntityType;
    id: string;
    label: string;
    relationId?: string;
    artifactType?: AnalyticsArtifactType;
    source?: ChartSourceType;
    slug?: string;
    spaceName?: string;
    chartKind?: string;
};

export type AiStructuredMessagePart =
    | AiStructuredMarkdownPart
    | AiStructuredReferencePart;

export type AiStructuredContext = {
    // Keep this as a literal so TSOA can generate the SubmitAgentChat schema.
    version: 1;
    parts: AiStructuredMessagePart[];
    connectionSelection?: AiConnectorSelection;
};

export type AiDataFieldReference = {
    table: string;
    column: string;
};

export const AI_DATA_FIELD_REFERENCE_ID_PATTERN = /^[^.\s]+\.\S+$/;

export const buildAiDataFieldReferenceId = (
    table: string,
    column: string,
): string => `${table}.${column}`;

export const parseAiDataFieldReferenceId = (
    referenceId: string,
): AiDataFieldReference | undefined => {
    if (!AI_DATA_FIELD_REFERENCE_ID_PATTERN.test(referenceId)) {
        return undefined;
    }

    const separatorIndex = referenceId.indexOf('.');

    return {
        table: referenceId.slice(0, separatorIndex),
        column: referenceId.slice(separatorIndex + 1),
    };
};

const markdownPartSchema = z.object({
    type: z.literal(AiStructuredMessagePartType.MARKDOWN),
    content: z.string(),
});

const referencePartSchema = z.object({
    type: z.literal(AiStructuredMessagePartType.REFERENCE),
    entityType: z.union([
        z.nativeEnum(AiEntityType),
        z.nativeEnum(AiStructuredReferenceEntityType),
    ]),
    id: z.string().min(1).max(500),
    label: z.string().min(1).max(500),
    relationId: z.string().min(1).max(500).optional(),
    artifactType: z.nativeEnum(AnalyticsArtifactType).optional(),
    source: z.nativeEnum(ChartSourceType).optional(),
    slug: z.string().min(1).max(500).optional(),
    spaceName: z.string().min(1).max(500).optional(),
    chartKind: z.string().min(1).max(100).optional(),
});
const uuidReferenceIdSchema = z.string().uuid();

export const aiStructuredContextSchema: z.ZodType<AiStructuredContext> = z
    .object({
        version: z.literal(AI_STRUCTURED_CONTEXT_VERSION),
        parts: z
            .array(z.union([markdownPartSchema, referencePartSchema]))
            .min(1)
            .max(100),
        connectionSelection: aiConnectorSelectionSchema.optional(),
    })
    .superRefine((context, refinement) => {
        const referenceCount = context.parts.filter(
            (part) => part.type === AiStructuredMessagePartType.REFERENCE,
        ).length;
        if (referenceCount > 20) {
            refinement.addIssue({
                code: 'custom',
                path: ['parts'],
                message: 'A message may contain at most 20 references.',
            });
        }

        context.parts.forEach((part, index) => {
            if (part.type !== AiStructuredMessagePartType.REFERENCE) return;

            if (
                part.entityType === AiEntityType.ANALYTICS_ARTIFACT &&
                !part.artifactType
            ) {
                refinement.addIssue({
                    code: 'custom',
                    path: ['parts', index, 'artifactType'],
                    message:
                        'Analytics artifact references require artifactType.',
                });
            }
            if (
                part.entityType === AiEntityType.DATA_FIELD &&
                !parseAiDataFieldReferenceId(part.id)
            ) {
                refinement.addIssue({
                    code: 'custom',
                    path: ['parts', index, 'id'],
                    message: 'Data field references require a table.column id.',
                });
            }
            if (
                part.entityType === AiEntityType.TRAIT &&
                !uuidReferenceIdSchema.safeParse(part.id).success
            ) {
                refinement.addIssue({
                    code: 'custom',
                    path: ['parts', index, 'id'],
                    message: 'Trait references require a UUID.',
                });
            }
        });
    });

export const buildAiStructuredContextPartMetadata = (
    context: AiStructuredContext | undefined,
): Record<string, unknown> =>
    context ? { [AI_STRUCTURED_CONTEXT_METADATA_KEY]: context } : {};

export const getAiStructuredContextFromPart = (
    part: OpenCodeMessagePart,
): AiStructuredContext | undefined => {
    if (part.type !== 'text' || !part.metadata) return undefined;

    const parsed = aiStructuredContextSchema.safeParse(
        part.metadata[AI_STRUCTURED_CONTEXT_METADATA_KEY],
    );
    return parsed.success ? parsed.data : undefined;
};

export const getAiStructuredContextFromMessage = (
    message: OpenCodeMessage,
): AiStructuredContext | undefined => {
    for (const part of message.parts) {
        const context = getAiStructuredContextFromPart(part);
        if (context) return context;
    }
    return undefined;
};

export const getStructuredContextVisibleText = (
    context: AiStructuredContext,
): string =>
    context.parts
        .map((part) =>
            part.type === AiStructuredMessagePartType.MARKDOWN
                ? part.content
                : part.label,
        )
        .join('');
